/**
 * useClarifiExport Hook
 *
 * React hook for managing Clarifi data tape exports
 */

import { useState, useCallback } from 'react';
import { createComponentLogger } from '@/utils/logger';
import type { ClarifiExportResult } from '@/types/clarifi-export';
import { exportAndDownloadClarifi } from '@/services/clarifiExportService';

const log = createComponentLogger('useClarifiExport', 'hooks');

interface UseClarifiExportReturn {
  /** Export and download a single loan */
  exportLoan: (loanNumber: string) => Promise<ClarifiExportResult>;
  /** Whether an export is currently in progress */
  isExporting: boolean;
  /** Last export result */
  lastResult: ClarifiExportResult | null;
  /** Error message if last export failed */
  error: string | null;
  /** Clear error state */
  clearError: () => void;
}

/**
 * Hook for exporting loans to Clarifi data tape format
 *
 * @example
 * ```tsx
 * function ExportButton({ loanNumber }: { loanNumber: string }) {
 *   const { exportLoan, isExporting, error } = useClarifiExport();
 *
 *   return (
 *     <Button
 *       onClick={() => exportLoan(loanNumber)}
 *       disabled={isExporting}
 *     >
 *       {isExporting ? 'Exporting...' : 'Export to Clarifi'}
 *     </Button>
 *   );
 * }
 * ```
 */
export function useClarifiExport(): UseClarifiExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [lastResult, setLastResult] = useState<ClarifiExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exportLoan = useCallback(async (loanNumber: string): Promise<ClarifiExportResult> => {
    setIsExporting(true);
    setError(null);

    log.info('Starting Clarifi export', { loanNumber });

    try {
      const result = await exportAndDownloadClarifi(loanNumber);

      setLastResult(result);

      if (!result.success) {
        setError(result.error || 'Export failed');
        log.warn('Export failed', { loanNumber, error: result.error });
      } else {
        log.info('Export completed', { loanNumber, filename: result.filename });
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed';
      log.error('Export exception', { loanNumber }, err as Error);
      setError(errorMessage);

      const failedResult: ClarifiExportResult = {
        success: false,
        filename: '',
        error: errorMessage,
        rowCount: 0
      };
      setLastResult(failedResult);

      return failedResult;
    } finally {
      setIsExporting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    exportLoan,
    isExporting,
    lastResult,
    error,
    clearError
  };
}
